/**
 * GameEndModal - Victory/Defeat screen component
 * Displays game results when a match concludes with team scores and player rankings
 */

import type { PlayerFinalStats } from "@game/state";
import { useGameState } from "@game/state";
import React, { useCallback, useEffect, useState } from "react";

/**
 * Team color mapping for CTF mode visual accents
 */
const TEAM_COLORS: Record<number, string> = {
  0: "#61afef", // Blue team
  1: "#e06c75", // Red team
  2: "#4ade80", // Green team
  3: "#e5c07b", // Yellow team
};

/**
 * Get CSS color variable for a team
 */
function getTeamColor(teamId: number | undefined): string {
  if (teamId === undefined) return "var(--accent)";
  return TEAM_COLORS[teamId] ?? "var(--accent)";
}

/**
 * Title section showing victory/defeat/draw status
 */
interface GameEndTitleProps {
  isWinner: boolean;
  isDraw: boolean;
  reason: string;
  localTeamId?: number;
}

function GameEndTitle({
  isWinner,
  isDraw,
  reason,
  localTeamId,
}: GameEndTitleProps): React.ReactElement {
  const getTitleClass = (): string => {
    if (isDraw) return "draw";
    if (isWinner) return "victory";
    return "";
  };

  const getTitleText = (): string => {
    if (isDraw) return "DRAW";
    if (isWinner) return "VICTORY";
    return "DEFEAT";
  };

  const getTitleColor = (): React.CSSProperties => {
    if (isDraw) return { color: "var(--warning)" };
    if (isWinner) return { color: getTeamColor(localTeamId) };
    return { color: "var(--danger)" };
  };

  return (
    <>
      <h2 className={getTitleClass()} style={getTitleColor()}>
        {getTitleText()}
      </h2>
      <p style={{ marginBottom: "16px", color: "var(--muted)" }}>{reason}</p>
    </>
  );
}

/**
 * Team scores display section for team-based game modes
 */
interface TeamScoresSectionProps {
  finalScores: Record<number, number>;
  localTeamId?: number;
  isCTF: boolean;
}

function TeamScoresSection({
  finalScores,
  localTeamId,
  isCTF,
}: TeamScoresSectionProps): React.ReactElement {
  const getTeamLabel = (teamId: number): string => {
    if (isCTF) {
      if (teamId === 0) return "BLUE TEAM";
      if (teamId === 1) return "RED TEAM";
      return `TEAM ${teamId}`;
    }
    return `TEAM ${teamId}`;
  };

  return (
    <div
      style={{
        marginBottom: "24px",
        padding: "16px",
        background: "var(--bg)",
        border: "1px solid var(--border)",
      }}
    >
      <div
        style={{
          fontSize: "14px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "1.5px",
          color: "var(--muted)",
          marginBottom: "12px",
        }}
      >
        {isCTF ? "FINAL CAPTURES" : "FINAL SCORES"}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          gap: "16px",
        }}
      >
        {Object.entries(finalScores).map(([teamIdStr, score]) => {
          const teamId = Number.parseInt(teamIdStr, 10);
          const teamColor = getTeamColor(teamId);
          const isLocalTeam = teamId === localTeamId;

          return (
            <div
              key={teamIdStr}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "12px",
                border: isLocalTeam
                  ? `2px solid ${teamColor}`
                  : "1px solid var(--border)",
                background: isLocalTeam
                  ? "rgba(255, 255, 255, 0.05)"
                  : "transparent",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "1.5px",
                  color: teamColor,
                  marginBottom: "4px",
                }}
              >
                {getTeamLabel(teamId)}
              </div>
              <div
                style={{
                  fontSize: "32px",
                  fontWeight: 700,
                  color: teamColor,
                  textShadow: `0 0 8px ${teamColor}40`,
                }}
              >
                {score}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Table header component for player rankings
 */
interface PlayerRankingsTableHeaderProps {
  isCTF: boolean;
}

function PlayerRankingsTableHeader({
  isCTF,
}: PlayerRankingsTableHeaderProps): React.ReactElement {
  const headerCellStyle: React.CSSProperties = {
    padding: "8px 4px",
    color: "var(--muted)",
    fontWeight: 700,
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "1px",
  };

  return (
    <thead>
      <tr
        style={{
          borderBottom: "1px solid var(--border)",
          position: "sticky",
          top: 0,
          background: "var(--bg)",
          zIndex: 5,
        }}
      >
        <th style={{ ...headerCellStyle, textAlign: "left" }}>RANK</th>
        <th style={{ ...headerCellStyle, textAlign: "left" }}>PLAYER</th>
        <th style={{ ...headerCellStyle, textAlign: "right" }}>KILLS</th>
        <th style={{ ...headerCellStyle, textAlign: "right" }}>DEATHS</th>
        <th style={{ ...headerCellStyle, textAlign: "right" }}>PELLETS</th>
        {isCTF && (
          <th style={{ ...headerCellStyle, textAlign: "right" }}>CAPS</th>
        )}
        <th style={{ ...headerCellStyle, textAlign: "right" }}>
          {isCTF ? "CAPS" : "SCORE"}
        </th>
      </tr>
    </thead>
  );
}

/**
 * Table row component for individual player stats
 */
interface PlayerRankingsTableRowProps {
  player: PlayerFinalStats;
  rank: number;
  isCurrentPlayer: boolean;
  hasTeams: boolean;
  isCTF: boolean;
}

function PlayerRankingsTableRow({
  player,
  rank,
  isCurrentPlayer,
  hasTeams,
  isCTF,
}: PlayerRankingsTableRowProps): React.ReactElement {
  const teamColor = hasTeams ? getTeamColor(player.teamId) : "var(--accent)";

  const stickyStyle: React.CSSProperties = isCurrentPlayer
    ? {
        position: "sticky",
        bottom: 0,
        backgroundColor: "rgba(255, 255, 255, 0.08)",
        border: "1px solid rgba(255, 255, 255, 0.4)",
        zIndex: 10,
      }
    : {
        borderBottom: "1px solid var(--border-secondary)",
      };

  return (
    <tr style={stickyStyle}>
      <td
        style={{
          padding: "8px 4px",
          color: "var(--muted)",
          fontWeight: 700,
        }}
      >
        #{rank}
      </td>
      <td
        style={{
          padding: "8px 4px",
          color: teamColor,
          fontWeight: isCurrentPlayer ? 700 : 600,
        }}
      >
        {player.playerName}
      </td>
      <td
        style={{
          padding: "8px 4px",
          textAlign: "right",
          color: "var(--fg)",
        }}
      >
        {player.kills}
      </td>
      <td
        style={{
          padding: "8px 4px",
          textAlign: "right",
          color: "var(--fg)",
        }}
      >
        {player.deaths}
      </td>
      <td
        style={{
          padding: "8px 4px",
          textAlign: "right",
          color: "var(--pellets)",
        }}
      >
        {player.pelletsCollected}
      </td>
      {isCTF && (
        <td
          style={{
            padding: "8px 4px",
            textAlign: "right",
            color: teamColor,
            fontWeight: 700,
          }}
        >
          {player.captures || 0}
        </td>
      )}
      <td
        style={{
          padding: "8px 4px",
          textAlign: "right",
          color: "var(--accent)",
          fontWeight: 700,
        }}
      >
        {isCTF ? player.captures || 0 : player.score}
      </td>
    </tr>
  );
}

/**
 * Player rankings table showing top 10 players plus current player if not in top 10
 */
interface PlayerRankingsTableProps {
  playerStats: PlayerFinalStats[];
  hasTeams: boolean;
  isCTF: boolean;
  localPlayerId?: string;
}

function PlayerRankingsTable({
  playerStats,
  hasTeams,
  isCTF,
  localPlayerId,
}: PlayerRankingsTableProps): React.ReactElement {
  const sortedPlayers = [...playerStats].sort((a, b) => {
    if (isCTF) {
      return (b.captures || 0) - (a.captures || 0);
    }
    return b.score - a.score;
  });
  const top10 = sortedPlayers.slice(0, 10);
  const currentPlayerIndex = sortedPlayers.findIndex(
    (p) => p.playerId === localPlayerId,
  );

  // Display logic: show top 10 + current player if not in top 10
  const displayPlayers: PlayerFinalStats[] = (() => {
    if (currentPlayerIndex === -1 || currentPlayerIndex < 10) {
      return top10;
    }
    const currentPlayer = sortedPlayers[currentPlayerIndex];
    if (!currentPlayer) {
      return top10;
    }
    return [...top10, currentPlayer];
  })();

  return (
    <div
      style={{
        marginBottom: "24px",
        padding: "16px",
        background: "var(--bg)",
        border: "1px solid var(--border)",
      }}
    >
      <div
        style={{
          fontSize: "14px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "1.5px",
          color: "var(--muted)",
          marginBottom: "12px",
        }}
      >
        PLAYER RANKINGS
      </div>
      <div
        style={{
          maxHeight: "400px",
          overflowY: "auto",
          border: "1px solid var(--border)",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
          }}
        >
          <PlayerRankingsTableHeader isCTF={isCTF} />
          <tbody>
            {displayPlayers.map((player) => {
              const isCurrentPlayer = player.playerId === localPlayerId;
              const actualRank =
                sortedPlayers.findIndex((p) => p.playerId === player.playerId) +
                1;

              return (
                <PlayerRankingsTableRow
                  key={player.playerId}
                  player={player}
                  rank={actualRank}
                  isCurrentPlayer={isCurrentPlayer}
                  hasTeams={hasTeams}
                  isCTF={isCTF}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Modal component displayed when game ends
 * Shows victory/defeat status, team scores (if applicable), and player statistics
 */
export function GameEndModal(): React.ReactElement | null {
  const gameEndData = useGameState((s) => s.gameEndData);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (gameEndData) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      setIsVisible(false);
    }
  }, [gameEndData]);

  const handleJoinNewGame = useCallback(() => {
    window.location.reload();
  }, []);

  if (!gameEndData) {
    return null;
  }

  const {
    isWinner,
    isDraw,
    reason,
    finalScores,
    playerStats,
    localTeamId,
    localPlayerId,
  } = gameEndData;

  const hasTeams = Object.keys(finalScores).length > 0;
  const isCTF = hasTeams && Object.keys(finalScores).length === 2;

  // Determine background color for CTF mode based on winning team
  const getModalBackground = (): string => {
    if (!isCTF || isDraw) {
      return "var(--bg)";
    }

    if (isWinner && localTeamId !== undefined) {
      const teamColor = getTeamColor(localTeamId);
      return `linear-gradient(135deg, var(--bg) 0%, ${teamColor}15 100%)`;
    }

    return "var(--bg)";
  };

  return (
    <div className={`modal-overlay ${isVisible ? "show" : ""}`}>
      <div
        className="modal"
        style={{
          background: getModalBackground(),
        }}
      >
        <GameEndTitle
          isWinner={isWinner}
          isDraw={isDraw}
          reason={reason}
          localTeamId={localTeamId}
        />

        {hasTeams && (
          <TeamScoresSection
            finalScores={finalScores}
            localTeamId={localTeamId}
            isCTF={isCTF}
          />
        )}

        <PlayerRankingsTable
          playerStats={playerStats}
          hasTeams={hasTeams}
          isCTF={isCTF}
          localPlayerId={localPlayerId}
        />

        <button type="button" onClick={handleJoinNewGame}>
          JOIN NEW GAME
        </button>
      </div>
    </div>
  );
}
